/**
 * Extends BpmnUpdater to handle parents with uninitialized flowElements (or other containment).
 * Fixes "Cannot read properties of undefined (reading 'push')" when dropping from Agentische Werkzeuge.
 */
import {
    add as collectionAdd,
    remove as collectionRemove
} from 'diagram-js/lib/util/Collections';
import { is } from 'bpmn-js/lib/util/ModelUtil';
import inherits from 'inherits-browser';
import BpmnUpdater from 'bpmn-js/lib/features/modeling/BpmnUpdater';

function getDefinitions(element) {
  while (element && !is(element, 'bpmn:Definitions')) {
    element = element.$parent;
  }
  return element;
}

function SafeBpmnUpdater(eventBus, bpmnFactory, connectionDocking) {
  BpmnUpdater.call(this, eventBus, bpmnFactory, connectionDocking);
}

inherits(SafeBpmnUpdater, BpmnUpdater);

SafeBpmnUpdater.$inject = ['eventBus', 'bpmnFactory', 'connectionDocking'];

/**
 * Override to ensure containment collection exists before pushing.
 * When newParent.get(containment) returns undefined (e.g. Process with uninitialized flowElements),
 * we initialize it first.
 */
SafeBpmnUpdater.prototype.updateSemanticParent = function (businessObject, newParent, visualParent) {
  let containment;

  if (businessObject.$parent === newParent) {
    return;
  }

  if (is(businessObject, 'bpmn:DataInput') || is(businessObject, 'bpmn:DataOutput')) {
    if (newParent && is(newParent, 'bpmn:Participant') && 'processRef' in newParent) {
      newParent = newParent.processRef;
    }
    if (newParent && 'ioSpecification' in newParent && newParent.ioSpecification === businessObject.$parent) {
      return;
    }
  }

  if (is(businessObject, 'bpmn:Lane')) {
    if (newParent) {
      newParent = this.getLaneSet(newParent);
    }
    containment = 'lanes';
  } else if (is(businessObject, 'bpmn:FlowElement')) {
    if (newParent) {
      if (is(newParent, 'bpmn:Participant')) {
        newParent = newParent.processRef;
      } else if (is(newParent, 'bpmn:Lane')) {
        do {
          newParent = newParent.$parent.$parent;
        } while (is(newParent, 'bpmn:Lane'));
      }
    }
    containment = 'flowElements';
  } else if (is(businessObject, 'bpmn:Artifact')) {
    while (newParent &&
           !is(newParent, 'bpmn:Process') &&
           !is(newParent, 'bpmn:SubProcess') &&
           !is(newParent, 'bpmn:Collaboration')) {
      if (is(newParent, 'bpmn:Participant')) {
        newParent = newParent.processRef;
        break;
      } else {
        newParent = newParent.$parent;
      }
    }
    containment = 'artifacts';
  } else if (is(businessObject, 'bpmn:MessageFlow')) {
    containment = 'messageFlows';
  } else if (is(businessObject, 'bpmn:Participant')) {
    containment = 'participants';
    const process = businessObject.processRef;
    if (process) {
      const definitions = getDefinitions(businessObject.$parent || newParent);
      const rootElements = definitions && definitions.get('rootElements');
      if (rootElements) {
        if (businessObject.$parent) {
          collectionRemove(rootElements, process);
          process.$parent = null;
        }
        if (newParent) {
          collectionAdd(rootElements, process);
          process.$parent = definitions;
        }
      }
    }
  } else if (is(businessObject, 'bpmn:DataOutputAssociation')) {
    containment = 'dataOutputAssociations';
  } else if (is(businessObject, 'bpmn:DataInputAssociation')) {
    containment = 'dataInputAssociations';
  }

  if (!containment) {
    throw new Error(`no parent for <${businessObject.id}> in <${newParent && newParent.id}>`);
  }

  let children;

  if (businessObject.$parent) {
    children = businessObject.$parent.get(containment);
    if (children && typeof children.indexOf === 'function' && typeof children.splice === 'function') {
      collectionRemove(children, businessObject);
    }
  }

  if (!newParent) {
    businessObject.$parent = null;
  } else {
    children = newParent.get(containment);
    if (!children || typeof children.push !== 'function') {
      children = children && typeof children[Symbol.iterator] === 'function' ? Array.from(children) : [];
      newParent.set(containment, children);
    }
    children.push(businessObject);
    businessObject.$parent = newParent;
  }

  if (visualParent) {
    let diChildren = visualParent.get(containment);
    if (children) {
      collectionRemove(children, businessObject);
    }
    if (newParent) {
      if (!diChildren || typeof diChildren.push !== 'function') {
        diChildren = diChildren && typeof diChildren[Symbol.iterator] === 'function' ? Array.from(diChildren) : [];
        newParent.set(containment, diChildren);
      }
      diChildren.push(businessObject);
    }
  }
};

export default SafeBpmnUpdater;
